/**
 * Pure text layout for the Text behavior.
 *
 * Single source of truth for glyph placement: the Text behavior feeds this
 * layout to Babylon's MSDF renderer via `addParagraph`'s `customLayoutEngine`
 * hook, derives its measured extents (anchoring, selection mesh) from it, and
 * exposes it to editors (caret placement, selection highlighting, hit
 * testing) through `Text.getEditLayout()`. Because the renderer consumes the
 * same numbers, editor carets are exact by construction.
 *
 * ## Coordinate conventions
 *
 * Layout runs in **font units** (BMFont atlas units; `info.size` per em) and
 * mirrors Babylon's `SdfTextParagraph`/`addParagraph` math:
 *
 * - Per line, glyphs advance left→right: `cursor += kerning` before a glyph,
 *   `cursor += xadvance + letterSpacing` after it.
 * - A glyph's rendered ink quad spans
 *   `x ∈ [g.x + xoffset, g.x + xoffset + width]`,
 *   `y ∈ [1 − g.y − yoffset − height, 1 − g.y − yoffset]`
 *   (the `1.0` is Babylon's font-unit y reference; y grows downward in BMFont,
 *   so the rendered convention flips it).
 * - Line `L` starts at `g.y = L × lineHeightUnits`.
 * - Per-line alignment and box anchoring shift every glyph by
 *   `(paragraphWidth − lineWidth) × anchorXFraction − anchorXFraction × paragraphWidth`.
 *
 * Whitespace is **pre**-like, matching the Text spec: spaces are preserved
 * verbatim — unlike Babylon's default layout engine, which collapses runs of
 * spaces and trims line ends (and which this module replaces). Tabs lay out
 * as a single space advance; `\r` is zero-width.
 *
 * ## Wrapping
 *
 * With no `maxWidthUnits` the paragraph breaks only at explicit `\n`. Given
 * one (the layout-resolved width, handed down by the Text behavior), each
 * explicit line that does not fit is re-broken greedily at spaces — see
 * `wrapExplicitLine` for the rule and its provenance.
 *
 * Node-local conversion: the Text behavior renders the paragraph through
 * `Scaling(scale) × Translation(0, yWorldOffset, 0)`, so
 * `local = fontUnits × (scale / emSize)` with `yWorldOffset` added to y.
 * `toEditLayout` applies that mapping so editor code works in the same space
 * the glyphs occupy under the text's parent node.
 */

/** Minimal BMFont glyph shape consumed by layout (any superset works). */
export interface LayoutFontChar {
    id: number;
    width: number;
    height: number;
    xoffset: number;
    yoffset: number;
    xadvance: number;
    /** Atlas page; Babylon's synthetic fallbacks (space/tofu) use -1. */
    page: number;
}

/** Font query surface, adapted from Babylon's FontAsset internals. */
export interface LayoutFontApi {
    getChar(charCode: number): LayoutFontChar;
    getKerning(first: number, second: number): number;
}

export interface TextLayoutParams {
    /** Vertical advance per line, font units (`common.lineHeight × renderer multiplier`). */
    lineHeightUnits: number;
    /** Extra advance appended after every glyph, font units. */
    letterSpacingUnits: number;
    /** Horizontal anchor/alignment fraction: left 0, center 0.5, right 1. */
    anchorXFraction: number;
    /**
     * Wrap constraint in font units. Absent or non-positive means no wrap:
     * lines break only at explicit `\n` (a standalone Text node, or one whose
     * `wrap` is false). Positive means every explicit line wider than this is
     * re-broken at spaces.
     */
    maxWidthUnits?: number;
    /**
     * Absolute slack, font units, allowed on the "already fits" fast path:
     * one layout unit (1 mm, `LAYOUT_UNIT_M`) expressed in this paragraph's
     * font units. Omitted means the relative solver-noise floor only.
     */
    wrapEpsilonUnits?: number;
}

/** A positioned glyph. Shape-compatible with Babylon's `SdfGlyph`. */
export interface LaidOutGlyph {
    char: LayoutFontChar;
    line: number;
    /** Index within the line's glyph list (Babylon contract). */
    position: number;
    /** Font units, post alignment/anchor shift. */
    x: number;
    /** Font units: `line × lineHeightUnits`. */
    y: number;
    /** Index of the source character this glyph renders. */
    sourceIndex: number;
}

/** A laid-out line. Shape-compatible with Babylon's `SdfTextLine`. */
export interface LaidOutLine {
    text: string;
    glyphs: LaidOutGlyph[];
    /** Source-string index of the line's first character. */
    start: number;
    /** Source-string index one past the last character (the `\n` is not included). */
    end: number;
    /** Advance width, font units (trailing letterSpacing trimmed). */
    width: number;
    /**
     * Caret boundary x positions, font units, post-shift.
     * `caretXs[i]` is the boundary before the line's i-th character;
     * length is `(end - start) + 1`.
     */
    caretXs: number[];
    /**
     * This line ends at a WRAP, not a `\n` — so the next line begins at
     * the same source index and no character separates them.
     */
    softBreak: boolean;
}

export interface TextLayout {
    text: string;
    lines: LaidOutLine[];
    glyphs: LaidOutGlyph[];
    /** Widest line advance width, font units. */
    width: number;
    /** `lines.length × lineHeightUnits`, font units. */
    height: number;
    params: TextLayoutParams;
}

const SPACE_CHAR_CODE = 32;

/**
 * Fit tolerance for the no-wrap fast path, as a fraction of the constraint.
 *
 * Our line widths are exact sums of integer font units, so the only noise on
 * this comparison is the float round-trip through the solver — a Hug width we
 * measured, resolved into a slot, and were handed back. That noise is
 * relative, not absolute. 1e-6 of the constraint is about seven orders above
 * it and, on a 1 m box, one micron: it cannot let a genuinely over-long line
 * through.
 *
 * It is only the noise guard. The tolerance that decides whether a line
 * "already fits" its slot is one layout unit (1 mm), absolute, which callers
 * pass as `wrapEpsilonUnits` — text is authored on a millimetre grid, so a
 * line overhanging by less than a millimetre is within the grid's own
 * resolution and must not be broken. This floor applies for callers that pass
 * none.
 */
const WRAP_EPSILON_RELATIVE = 1e-6;

/** One laid-out line's slice of the source string. */
interface LineSegment {
    text: string;
    /** Index of `text[0]` in the source string. */
    start: number;
    /**
     * True when the line ends at a WRAP rather than at an explicit `\n` or
     * the end of the text. Soft-broken lines drop their trailing spaces from
     * the measured width (the spaces stay in the line's source range so caret
     * boundaries remain contiguous — they hang past the line end, as in a
     * textarea).
     */
    softBreak: boolean;
}

/** A running advance measurement, mirroring the glyph loop's rules exactly. */
interface RunMeasure {
    /** Advance including the trailing letterSpacing of the last glyph. */
    cursor: number;
    lastId: number | undefined;
    glyphs: number;
}

function measureAppend(
    run: RunMeasure,
    s: string,
    font: LayoutFontApi,
    letterSpacingUnits: number,
): void {
    for (let i = 0; i < s.length; i++) {
        let code = s.charCodeAt(i);
        if (code === 9) code = SPACE_CHAR_CODE; // tab → single space advance
        if (code === 13) continue; // \r is zero-width
        const ch = font.getChar(code);
        if (run.lastId !== undefined) run.cursor += font.getKerning(run.lastId, ch.id);
        run.cursor += ch.xadvance + letterSpacingUnits;
        run.lastId = ch.id;
        run.glyphs++;
    }
}

/** Visible advance width of a run (the invisible trailing letterSpacing off). */
function runWidth(run: RunMeasure, letterSpacingUnits: number): number {
    return Math.max(0, run.cursor - (run.glyphs > 0 ? letterSpacingUnits : 0));
}

/**
 * Break one explicit line to fit `maxWidthUnits`, greedily, at spaces.
 *
 * The rule, as stated in `docs/layout/LAYOUT_ENGINE.md`:
 *
 * - **Fast path** — a line that already fits (within `wrapEpsilonUnits`)
 *   stays verbatim, so enabling wrap can never add a break to text that did
 *   not need one.
 * - **Greedy fill** — words are appended while they fit; the first word that
 *   does not starts the next line. No epsilon inside the loop, deliberately:
 *   the slack exists to stop a whole paragraph re-breaking on rounding, not
 *   to let individual words overhang.
 * - **No hyphenation, no mid-word breaking** — a single word wider than the
 *   constraint occupies its own line and overflows it.
 *
 * A break SLICES the source rather than rejoining words with single spaces:
 * whitespace is pre-like per the Text spec (spaces render verbatim, and the
 * editor's caret boundaries are source indices), so the spaces at a break
 * stay in the earlier line's range and hang past its end. For ordinary
 * single-spaced prose slicing and rejoining give identical breaks and
 * identical line widths.
 */
function wrapExplicitLine(
    lineText: string,
    lineStart: number,
    maxWidthUnits: number,
    font: LayoutFontApi,
    letterSpacingUnits: number,
    wrapEpsilonUnits: number,
): LineSegment[] {
    const fastPathLimit = Math.max(maxWidthUnits * (1 + WRAP_EPSILON_RELATIVE), maxWidthUnits + wrapEpsilonUnits);
    const whole: RunMeasure = { cursor: 0, lastId: undefined, glyphs: 0 };
    measureAppend(whole, lineText, font, letterSpacingUnits);
    if (runWidth(whole, letterSpacingUnits) <= fastPathLimit) {
        return [{ text: lineText, start: lineStart, softBreak: false }];
    }

    const segments: LineSegment[] = [];
    let segStart = 0;
    let placedEnd = -1; // end of the last word placed in the current segment
    let run: RunMeasure = { cursor: 0, lastId: undefined, glyphs: 0 };

    for (let i = 0; i < lineText.length;) {
        if (lineText.charCodeAt(i) === SPACE_CHAR_CODE) { i++; continue; }
        const wordStart = i;
        while (i < lineText.length && lineText.charCodeAt(i) !== SPACE_CHAR_CODE) i++;
        const wordEnd = i;

        // Candidate = the current segment extended to this word, spaces and
        // all. Measured incrementally, so each character is walked at most
        // twice across the whole line.
        // Measured from the segment start (leading spaces included — they are
        // rendered) the first time, from the previous word's end after that.
        const candidate: RunMeasure = { ...run };
        measureAppend(candidate, lineText.slice(placedEnd < 0 ? segStart : placedEnd, wordEnd), font, letterSpacingUnits);

        if (placedEnd > segStart && runWidth(candidate, letterSpacingUnits) > maxWidthUnits) {
            segments.push({
                text: lineText.slice(segStart, wordStart),
                start: lineStart + segStart,
                softBreak: true,
            });
            segStart = wordStart;
            run = { cursor: 0, lastId: undefined, glyphs: 0 };
            measureAppend(run, lineText.slice(wordStart, wordEnd), font, letterSpacingUnits);
        } else {
            run = candidate;
        }
        placedEnd = wordEnd;
    }

    // The tail — plus its trailing spaces — always lands, even when the line
    // held no words at all (a run of spaces wider than the constraint):
    // a paragraph must never lose a line to wrapping.
    segments.push({ text: lineText.slice(segStart), start: lineStart + segStart, softBreak: false });
    return segments;
}

/**
 * Lay out `text` with pre-like whitespace semantics, wrapped to
 * `params.maxWidthUnits` when one is given. Pure — safe to call at editor
 * interaction rates.
 */
export function computeTextLayout(
    text: string,
    params: TextLayoutParams,
    font: LayoutFontApi,
): TextLayout {
    const { lineHeightUnits, letterSpacingUnits, anchorXFraction, maxWidthUnits } = params;
    const wrapEpsilonUnits = params.wrapEpsilonUnits ?? 0;

    const lines: LaidOutLine[] = [];
    const segments: LineSegment[] = [];
    {
        let sourceIndex = 0;
        for (const lineText of text.split('\n')) {
            if (maxWidthUnits !== undefined && maxWidthUnits > 0) {
                segments.push(...wrapExplicitLine(
                    lineText, sourceIndex, maxWidthUnits, font, letterSpacingUnits, wrapEpsilonUnits,
                ));
            } else {
                segments.push({ text: lineText, start: sourceIndex, softBreak: false });
            }
            sourceIndex += lineText.length + 1; // +1 consumes the '\n'
        }
    }

    for (let lineIdx = 0; lineIdx < segments.length; lineIdx++) {
        const segment = segments[lineIdx];
        const lineText = segment.text;
        const glyphs: LaidOutGlyph[] = [];
        const caretXs: number[] = [0];
        const y = lineIdx * lineHeightUnits;

        let cursor = 0;
        let lastId: number | undefined;
        let lastGlyphBoundary = -1;
        let widthToLastNonSpace = 0;
        for (let i = 0; i < lineText.length; i++) {
            let code = lineText.charCodeAt(i);
            if (code === 9) code = SPACE_CHAR_CODE; // tab → single space advance
            if (code === 13) {
                // \r (CRLF leftovers) is zero-width: no glyph, boundary stays put.
                caretXs.push(cursor);
                continue;
            }
            const ch = font.getChar(code);
            const kerning = lastId !== undefined ? font.getKerning(lastId, ch.id) : 0;
            cursor += kerning;
            glyphs.push({
                char: ch,
                line: lineIdx,
                position: glyphs.length,
                x: cursor,
                y,
                sourceIndex: segment.start + i,
            });
            cursor += ch.xadvance + letterSpacingUnits;
            if (code !== SPACE_CHAR_CODE) widthToLastNonSpace = cursor - letterSpacingUnits;
            lastId = ch.id;
            caretXs.push(cursor);
            lastGlyphBoundary = caretXs.length - 1;
        }

        // The advance after the last glyph has no following glyph, so the
        // trailing letterSpacing is invisible: trim it from the line width
        // and pull the last glyph's boundary back with it — along with any
        // zero-width echoes after it (a trailing \r repeats the cursor).
        // A soft-broken line additionally drops the spaces it broke at, so
        // the paragraph's own width stays inside the constraint.
        const width = segment.softBreak
            ? Math.max(0, widthToLastNonSpace)
            : Math.max(0, cursor - (glyphs.length > 0 ? letterSpacingUnits : 0));
        if (lastGlyphBoundary >= 0 && !segment.softBreak) {
            const trimmed = Math.max(caretXs[lastGlyphBoundary - 1] ?? 0, width);
            for (let i = lastGlyphBoundary; i < caretXs.length; i++) {
                caretXs[i] = trimmed;
            }
        }

        lines.push({
            text: lineText,
            glyphs,
            start: segment.start,
            end: segment.start + lineText.length,
            width,
            caretXs,
            softBreak: segment.softBreak,
        });
    }

    const paragraphWidth = Math.max(0, ...lines.map((l) => l.width));

    // Per-line alignment within the box + box anchoring, in one shift —
    // same composition Babylon's engine applies (align anchor + translate.x).
    for (const line of lines) {
        const shift =
            (paragraphWidth - line.width) * anchorXFraction -
            anchorXFraction * paragraphWidth;
        for (const glyph of line.glyphs) glyph.x += shift;
        for (let i = 0; i < line.caretXs.length; i++) line.caretXs[i] += shift;
    }

    return {
        text,
        lines,
        glyphs: lines.flatMap((l) => l.glyphs),
        width: paragraphWidth,
        height: lines.length * lineHeightUnits,
        params,
    };
}

export interface TextLineBoxExtents {
    /** Widest line advance width, font units (same basis as the ink width). */
    width: number;
    /**
     * Top of the box, font units. Babylon's font-unit y reference (1.0) — the
     * font's line top — except for a paragraph anchored at its top, where it is
     * the face's cap line: the reference minus the face's `capTop`.
     */
    yTop: number;
    /** Bottom of the last line box: `yTop − lineCount × lineHeightUnits`. */
    yBot: number;
    /** Number of laid-out lines. */
    lineCount: number;
}

/**
 * Line-box extents of a layout: every line contributes exactly
 * `lineHeightUnits` of height, so the box is `lineCount × lineHeight ×
 * fontSize` whatever glyphs the lines contain. Two same-size labels measure
 * the same height whether or not one carries a descender, and leading is part
 * of the box — the CSS line-box model the Text spec states, and the model the
 * layout solver, the editor and a headless export all read.
 *
 * Ink is deliberately NOT a term in the box's HEIGHT. A glyph may be drawn
 * outside its line box (a font's ink depth routinely exceeds a `lineHeight` of
 * 1.0, and bracket-shaped glyphs exceed even 1.2), exactly as in CSS, where
 * overflow is visible and does not resize the element. Flooring the height on
 * ink instead makes a paragraph's measured height depend on which characters
 * it happens to contain, which breaks the "same size, same height" rule the
 * spec and the layout system are both written against. A face-wide floor was
 * tried and measured against the reference layouts: it put following siblings
 * 1-13 mm off their positions, where the plain box matches to the millimetre.
 *
 * The face's cap line IS a term in where the box starts, for a paragraph
 * anchored at its top. A BMFont face keeps empty space between its line top
 * and its capitals (a quarter to a third of an em in the bundled faces), and
 * `anchorY: "top"` puts the origin at the top of the first line, which an
 * author reads as the top of the letters: a label set against a card's top
 * edge should not float a quarter em below it. So `yTop` is `1.0 − capTop`,
 * where `capTop` is the face's cap-line depth (`capTopFontUnits`: the ink top
 * of its 'H'). It is one number per face, never read from the string: the
 * first line of "Once" and of "once" sit on the same baseline, an empty or
 * whitespace-only first line keeps the paragraph where it was, and a glyph
 * taller than a capital (an ascender, an accent, a bracket) rises above the
 * box exactly as a descender falls below it. Reading the first line's own ink
 * instead moved the whole paragraph up to 0.19 em whenever an edit changed the
 * tallest letter in it. Only the top moves: the height below is still
 * `lineCount × lineHeight`, and the anchor offset cancels the shift out of the
 * centre, so the measured size and centre are what they were, for every anchor,
 * headless (registry `capTop`) or rendered (the atlas's, the same number).
 *
 * The other anchors keep the font's line top. Starting their box at the cap
 * line would slide the whole glyph run up against the anchor for them too
 * (0.34 em for Roboto), so a `middle` label would sit above its pivot.
 *
 * The caret and selection bands `toEditLayout` exposes tile down from each
 * line's own line top and take the same anchor offset as the glyphs, so a
 * caret has the same shape against its letters whatever the anchor. For a
 * top-anchored paragraph they therefore start `capTop` above this box.
 */
export function computeLineBoxExtents(
    layout: TextLayout,
    anchorY: 'top' | 'middle' | 'bottom',
    capTop: number,
): TextLineBoxExtents {
    const lineCount = layout.lines.length;
    const yTop = 1.0 - (anchorY === 'top' ? capTop : 0);
    return {
        width: layout.width,
        yTop,
        yBot: yTop - lineCount * layout.params.lineHeightUnits,
        lineCount,
    };
}

// ============================================================================
// EDITOR-FACING LAYOUT (node-local space)
// ============================================================================

export interface EditLayoutLine {
    /** Source-string index range of the line (excluding the `\n`). */
    start: number;
    end: number;
    /** Full line band in node-local units (top > bottom; y up). */
    yTop: number;
    yBottom: number;
    /** Horizontal advance range of the line, node-local. */
    xMin: number;
    xMax: number;
    /** Caret boundary positions, node-local. length === (end - start) + 1. */
    caretXs: number[];
    /**
     * This line ends at a soft wrap rather than a `\n`. The next line then
     * starts at the SAME source index — there is no character between them —
     * which caret placement and selection both have to know about.
     */
    softBreak: boolean;
}

/**
 * A text layout converted to node-local space (the space of the node the
 * TextRenderer is parented to — see `Text.getTextRootNode()`), for caret
 * placement, selection rendering, and pointer hit-testing.
 */
export interface TextEditLayout {
    text: string;
    /** One em in node-local units. */
    em: number;
    lines: EditLayoutLine[];
    /** Union of the line bands, node-local. */
    bounds: { xMin: number; xMax: number; yTop: number; yBottom: number };
}

export interface EditCaretRect {
    line: number;
    x: number;
    yTop: number;
    yBottom: number;
}

export interface EditSelectionRect {
    line: number;
    xMin: number;
    xMax: number;
    yTop: number;
    yBottom: number;
}

/**
 * Convert a font-unit layout to node-local space.
 *
 * `unitScale` = `worldScale / emSize` (font units → node-local),
 * `yWorldOffset` is the anchor translation the behavior bakes into the
 * paragraph world matrix, `emSize` is the font's em-square in font units.
 *
 * Line bands span the full line advance (`lineHeightUnits`), like a
 * textarea's selection — consecutive lines tile with no gaps.
 */
export function toEditLayout(
    layout: TextLayout,
    unitScale: number,
    yWorldOffset: number,
    emSize: number,
): TextEditLayout {
    const lhU = layout.params.lineHeightUnits;
    const lines: EditLayoutLine[] = layout.lines.map((line, i) => {
        const yTopFont = 1.0 - i * lhU;
        return {
            start: line.start,
            end: line.end,
            yTop: yTopFont * unitScale + yWorldOffset,
            yBottom: (yTopFont - lhU) * unitScale + yWorldOffset,
            xMin: line.caretXs[0] * unitScale,
            xMax: line.caretXs[line.caretXs.length - 1] * unitScale,
            caretXs: line.caretXs.map((x) => x * unitScale),
            softBreak: line.softBreak,
        };
    });

    let xMin = Infinity, xMax = -Infinity;
    for (const l of lines) {
        if (l.xMin < xMin) xMin = l.xMin;
        if (l.xMax > xMax) xMax = l.xMax;
    }
    if (xMin === Infinity) {
        xMin = 0;
        xMax = 0;
    }

    return {
        text: layout.text,
        em: emSize * unitScale,
        lines,
        bounds: {
            xMin,
            xMax,
            yTop: lines[0]?.yTop ?? 0,
            yBottom: lines[lines.length - 1]?.yBottom ?? 0,
        },
    };
}

/** Caret rect for a source-string boundary index (0 … text.length). */
export function caretRectForIndex(layout: TextEditLayout, index: number): EditCaretRect {
    const clamped = Math.max(0, Math.min(index, layout.text.length));
    for (let i = 0; i < layout.lines.length; i++) {
        const line = layout.lines[i];
        // At a SOFT break the two lines share a boundary index — `prev.end`
        // and `next.start` are the same number with no character between.
        // Claiming it for the earlier line puts the caret at the end of the
        // line ABOVE when someone clicks the start of a wrapped line, which
        // is the wrong end of the wrap. Affinity goes to the next line, as
        // it does in a browser; a hard newline still ends its own line.
        if (line.softBreak && clamped === line.end && i + 1 < layout.lines.length) continue;
        if (clamped <= line.end) {
            const local = Math.max(0, clamped - line.start);
            return {
                line: i,
                x: line.caretXs[Math.min(local, line.caretXs.length - 1)],
                yTop: line.yTop,
                yBottom: line.yBottom,
            };
        }
    }
    const last = layout.lines[layout.lines.length - 1];
    return {
        line: layout.lines.length - 1,
        x: last.caretXs[last.caretXs.length - 1],
        yTop: last.yTop,
        yBottom: last.yBottom,
    };
}

/**
 * Nearest caret boundary for a node-local point. Clamps outside points to
 * the nearest line/boundary, so it never fails — pair with a bounds check
 * when "outside the text" should mean something else.
 */
export function indexForLocalPoint(layout: TextEditLayout, x: number, y: number): number {
    // Lines are ordered top → bottom (yTop descending).
    let lineIdx = layout.lines.length - 1;
    for (let i = 0; i < layout.lines.length; i++) {
        if (y >= layout.lines[i].yBottom) {
            lineIdx = i;
            break;
        }
    }
    const line = layout.lines[lineIdx];
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < line.caretXs.length; i++) {
        const d = Math.abs(line.caretXs[i] - x);
        if (d < bestDist) {
            bestDist = d;
            best = i;
        }
    }
    return line.start + best;
}

/**
 * Word range around boundary `index`, for double-click word selection.
 * Word characters are Unicode letters/digits/underscore; a run of
 * non-word characters (excluding newlines) also counts as one "word",
 * matching textarea double-click behavior.
 */
export function wordRangeAt(text: string, index: number): [number, number] {
    if (text.length === 0) return [0, 0];
    const isWord = (ch: string): boolean => /[\p{L}\p{N}_]/u.test(ch);

    // A boundary touches up to two chars; prefer the one that's a word char
    // (clicking right after a word selects that word, like a textarea).
    let probe = Math.min(index, text.length - 1);
    if ((index >= text.length || !isWord(text[index])) && index > 0 && isWord(text[index - 1])) {
        probe = index - 1;
    }
    if (text[probe] === '\n') return [index, index];

    const targetIsWord = isWord(text[probe]);
    const matches = (ch: string): boolean =>
        targetIsWord ? isWord(ch) : !isWord(ch) && ch !== '\n';

    let start = probe;
    while (start > 0 && matches(text[start - 1])) start--;
    let end = probe + 1;
    while (end < text.length && matches(text[end])) end++;
    return [start, end];
}

/**
 * Per-line highlight rects for a source-index range. Empty lines inside the
 * range and selected `\n`s get a small visible tail, like a textarea.
 */
export function selectionRectsForRange(
    layout: TextEditLayout,
    start: number,
    end: number,
): EditSelectionRect[] {
    if (end < start) [start, end] = [end, start];
    const rects: EditSelectionRect[] = [];
    const newlineTail = layout.em * 0.25;

    for (let i = 0; i < layout.lines.length; i++) {
        const line = layout.lines[i];
        if (end < line.start) break;
        const a = Math.max(start, line.start);
        const b = Math.min(end, line.end);
        if (a > b) continue;
        // This line's own '\n' is inside the range → draw the tail. A SOFT
        // break has no '\n' to select: the next line starts at the same
        // index, so a tail here would draw a phantom newline AND double-
        // highlight the boundary character on the line below.
        const includesNewline = !line.softBreak && start <= line.end && end > line.end;
        if (a === b && !includesNewline) continue;

        const xMin = line.caretXs[a - line.start];
        let xMax = line.caretXs[b - line.start];
        if (includesNewline) xMax += newlineTail;
        rects.push({ line: i, xMin, xMax, yTop: line.yTop, yBottom: line.yBottom });
    }
    return rects;
}
